import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Button, Alert, Tooltip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem, GridToolbar } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VideocamIcon from '@mui/icons-material/Videocam';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import EditCameraDialog from '../components/EditCameraDialog';
import AddCameraDialog from '../components/AddCameraDialog';
import { DataGridSkeleton } from '../components/LoadingSkeletons';
import CustomNoRowsOverlay from '../components/CustomNoRowsOverlay';
import PageHeader from '../components/PageHeader';

interface Camera {
  id: number;
  name: string;
  source: string;
  location?: string;
  school_id: number;
}

const Cameras: React.FC = () => {
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [loading, setLoading] = useState(true);
  const [editCamera, setEditCamera] = useState<Camera | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [error, setError] = useState('');
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const schoolId = 1; // TODO: Get from user context

  const fetchCameras = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await api.get(`/cameras/?school_id=${schoolId}`);
      setCameras(response.data as Camera[]);
    } catch (err) {
      setError('Failed to load cameras');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, schoolId]);

  useEffect(() => {
    fetchCameras();
  }, [fetchCameras]);

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/cameras/${id}`);
      fetchCameras();
    } catch {
      setError('Failed to delete camera');
    }
    setDeleteConfirmation({ open: false, id: null });
  };

  const openDeleteDialog = (id: number) => {
    setDeleteConfirmation({ open: true, id: id });
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
    { field: 'source', headerName: 'Source', flex: 1, minWidth: 200 },
    { field: 'location', headerName: 'Location', flex: 1, minWidth: 150 },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params) => [
        <Tooltip title="Live View" arrow>
          <GridActionsCellItem
            icon={<VideocamIcon />}
            label="Live View"
            aria-label="Live View"
            onClick={() => navigate(`/live/${params.row.id}`)}
          />
        </Tooltip>,
        <Tooltip title="Edit" arrow>
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            aria-label="Edit"
            onClick={() => setEditCamera(params.row)}
          />
        </Tooltip>,
        <Tooltip title="Delete" arrow>
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            aria-label="Delete"
            onClick={() => openDeleteDialog(params.row.id)}
          />
        </Tooltip>,
      ],
    },
  ];

  if (!isAuthenticated) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Cameras</Typography>
        <Alert severity="info">Please log in to view cameras.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Cameras</Typography>
        <DataGridSkeleton />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="Cameras">
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Camera
        </Button>
      </PageHeader>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={cameras}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          slots={{ 
            toolbar: GridToolbar,
            noRowsOverlay: () => <CustomNoRowsOverlay message="No Cameras Found" />
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          disableRowSelectionOnClick
        />
      </Box>

      {editCamera && (
        <EditCameraDialog
          open={!!editCamera}
          onClose={() => setEditCamera(null)}
          camera={editCamera}
          onSaved={fetchCameras}
        />
      )}

      <AddCameraDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        schoolId={schoolId}
        onSuccess={fetchCameras}
      />

      <Dialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, id: null })}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this camera? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmation({ open: false, id: null })}>Cancel</Button>
          <Button onClick={() => handleDelete(deleteConfirmation.id!)} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cameras; 